import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  Card,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Icon,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Txt,
} from "@repo/ui";
import { CashflowReport } from "@repo/ui/blocks";
import { isCurrency } from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { differenceInDays } from "date-fns";
import { Suspense, useState } from "react";
import { PiBuildingOffice, PiCaretDown, PiCaretRight } from "react-icons/pi";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  MatchLedgerTransactionFragment,
  MatchProposalFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const PAGE_SIZE = 20;

const orgsDocument = graphql(`
  query AdminExternalTxMatchProposalOrgs {
    organisations {
      id
      name
      squareLogoUrl
    }
  }
`);

const proposalsDocument = graphql(`
  query AdminExternalTransactionMatchProposals(
    $first: Int!
    $after: String
    $organisationIds: [ID!]
  ) {
    externalTransactionMatchProposals(
      first: $first
      after: $after
      filter: { organisationIds: $organisationIds }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      externalTransactionMatchProposals {
        ...MatchProposal
      }
    }
  }

  fragment MatchLedgerTransaction on LedgerTransaction {
    id
    shortRef
    effectiveAt
    description
    ledgerEntries {
      direction
      unitAmount {
        amount
        unit
      }
      ledgerAccount {
        id
        ... on AssetLedgerAccount {
          assetLedgerAccountKind
          asset {
            ...AssetCardFields
          }
        }
      }
    }
  }

  fragment MatchProposal on ExternalTransactionMatchProposal {
    id
    shortRef
    ledgerTransaction {
      ...MatchLedgerTransaction
    }
    matchedLedgerTransaction {
      ...MatchLedgerTransaction
    }
  }

`);

export const Route = createFileRoute("/__auth/cashflow-reports")({
  head: () => opsHead("Cashflow Reports"),
  validateSearch: z.object({
    organisationId: z
      .string()
      .optional()
      .catch(undefined)
      .default("org_3AMh9ghkteDrGmLQrqTCB6Rs9nH"),
  }),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function LedgerTransactionCell({ lt }: { lt: MatchLedgerTransactionFragment }) {
  const primaryEntry =
    lt.ledgerEntries?.find((e) => {
      if (
        e.ledgerAccount &&
        e.ledgerAccount?.__typename === "AssetLedgerAccount"
      ) {
        return e.ledgerAccount.assetLedgerAccountKind === "BALANCE";
      }
      return false;
    }) ?? lt.ledgerEntries?.[0];
  const asset =
    primaryEntry?.ledgerAccount?.__typename === "AssetLedgerAccount"
      ? primaryEntry?.ledgerAccount?.asset
      : null;
  const amount = primaryEntry?.unitAmount;

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/transactions/$transactionId"
          params={{ transactionId: lt.id }}
          className="font-mono text-info text-sm hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          #{lt.shortRef}
        </Link>
        <DateUI date={lt.effectiveAt} variant="DATE" />
      </div>
      {asset && <AccountCard account={asset} variant="LABEL" />}
      {lt.description && (
        <p className="text-secondary text-xs">{lt.description}</p>
      )}
      {amount && isCurrency(amount.unit) && (
        <div className="flex items-center justify-end gap-1 text-sm">
          <span
            className={
              primaryEntry?.direction === "CREDIT"
                ? "text-success"
                : "text-danger"
            }
          >
            {primaryEntry?.direction === "CREDIT" ? "CR" : "DB"}
          </span>
          <CurrencyAmountUI amount={amount.amount} currency={amount.unit} />
        </div>
      )}
    </Card>
  );
}

const columns: ColumnDef<MatchProposalFragment>[] = [
  {
    id: "ledgerTransaction",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Txn ID" />
    ),
    cell: ({ row }) => (
      <LedgerTransactionCell lt={row.original.ledgerTransaction} />
    ),
  },
  {
    id: "delta",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Delta" />
    ),
    cell: ({ row }) => {
      const { ledgerTransaction: lt, matchedLedgerTransaction: mlt } =
        row.original;
      const days = Math.abs(differenceInDays(lt.effectiveAt, mlt.effectiveAt));

      const getDebitAmount = (
        entries?: MatchLedgerTransactionFragment["ledgerEntries"] | null,
      ) => {
        if (!entries?.length) {
          return null;
        }
        const entry =
          entries.find((e) => e.direction === "DEBIT") ?? entries[0];
        return entry.unitAmount;
      };
      const a1 = getDebitAmount(row.original.ledgerTransaction.ledgerEntries);
      const a2 = getDebitAmount(
        row.original.matchedLedgerTransaction.ledgerEntries,
      );
      if (!a1 || !a2 || a1.unit !== a2.unit) {
        return <div className="text-right text-secondary text-sm">—</div>;
      }
      const delta = a1.amount.minus(a2.amount).abs();

      return (
        <div>
          <div className="text-center text-sm">
            {days < 1 ? "<1" : days} {days === 1 ? "day" : "days"}
          </div>
          <div className="text-center text-sm">
            {isCurrency(a1.unit) ? (
              <CurrencyAmountUI amount={delta} currency={a1.unit} />
            ) : (
              `${delta.toFixed(2)} ${a1.unit}`
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: "matchedLedgerTransaction",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Match Txn ID" />
    ),
    cell: ({ row }) => (
      <LedgerTransactionCell lt={row.original.matchedLedgerTransaction} />
    ),
  },
];

function View() {
  const { organisationId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: orgsData } = useSuspenseQuery(orgsDocument);

  const organisations = [...(orgsData?.organisations ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const setOrganisationId = (id: string) => {
    void navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        organisationId: id,
      }),
    });
  };

  return (
    <AdminPageContainer fullWidth testId="admin-cashflow-reports">
      <AdminPageHeader
        title="Cashflow Reports"
        subtitle="Cashflow report by entity, and external transaction match proposals"
        rightContent={
          <Select value={organisationId} onValueChange={setOrganisationId}>
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Select an organisation..." />
            </SelectTrigger>
            <SelectContent>
              {organisations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  <div className="flex flex-row gap-2">
                    <Icon
                      size="5"
                      imageUrl={org?.squareLogoUrl ?? undefined}
                      fallbackIcon={<PiBuildingOffice />}
                    />
                    {org.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <div className="flex flex-col gap-6">
        {organisationId && (
          <CashflowReport
            organisationId={organisationId}
            assetLinkPrefix="/assets"
            debug
            showUnattr
          />
        )}
        {organisationId && <ProposalsList organisationId={organisationId} />}
      </div>
    </AdminPageContainer>
  );
}

function ProposalsList({ organisationId }: { organisationId: string }) {
  const { data, loading, networkStatus, fetchMore } = useQuery(
    proposalsDocument,
    {
      variables: {
        first: PAGE_SIZE,
        after: null,
        organisationIds: [organisationId],
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const proposals =
    data?.externalTransactionMatchProposals.externalTransactionMatchProposals ??
    [];
  const pageInfo = data?.externalTransactionMatchProposals.pageInfo;
  const totalCount = data?.externalTransactionMatchProposals.totalCount;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <PiCaretDown className="shrink-0 text-tertiary" />
          ) : (
            <PiCaretRight className="shrink-0 text-tertiary" />
          )}
          <Txt variant="section-title">
            Transaction match proposals
            {totalCount != null ? ` (${totalCount})` : ""}
          </Txt>
        </button>
        {expanded && (
          <>
            <DataTable
              data={showAll ? proposals : proposals.slice(0, 5)}
              columns={columns}
              isLoadingData={
                loading && networkStatus !== NetworkStatus.fetchMore
              }
              manualPagination
              hidePagination
              showCsvExport={false}
              pageSize={showAll ? PAGE_SIZE : 5}
              emptyState={
                <Txt variant="body">
                  No match proposals found for this organisation.
                </Txt>
              }
            />
            {(!showAll || pageInfo?.hasNextPage) && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!showAll) {
                      setShowAll(true);
                    } else {
                      void fetchMore({
                        variables: { after: pageInfo?.endCursor },
                      });
                    }
                  }}
                  loading={networkStatus === NetworkStatus.fetchMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
